import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTrophy, FaChevronLeft, FaChevronRight,
  FaStar, FaCalendarAlt, FaLock, FaTimes,
  FaMedal, FaTicketAlt, FaGift, FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://api.dreamtoday.in";

/* ═══════════════════════════════════════════════════
   PALETTE
═══════════════════════════════════════════════════ */
const G = {
  gold:        "#C9A84C",
  goldDeep:    "#A8863A",
  goldLight:   "#E8C96A",
  goldGlow:    "rgba(201,168,76,0.22)",
  cream:       "#FFFDF5",
  creamDeep:   "#FFF8E7",
  creamMid:    "#FFF1CC",
  creamBorder: "#F0D98C",
  text:        "#5C4A1E",
  textMid:     "#8A6E34",
  textLight:   "#B89A5A",
};

/* position medal colours */
const MEDAL = {
  1: { bg: "linear-gradient(135deg,#FFD700,#FFA500)", color: "#7A4F00", label: "1st" },
  2: { bg: "linear-gradient(135deg,#C0C0C0,#A8A8A8)", color: "#3A3A3A", label: "2nd" },
  3: { bg: "linear-gradient(135deg,#CD7F32,#A0522D)", color: "#FFF8E7", label: "3rd" },
};

/* ═══════════════════════════════════════════════════
   FETCH HOOK
═══════════════════════════════════════════════════ */
const useCompletedCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");

  useEffect(() => {
    Promise.all(
      ["daily", "weekly", "monthly"].map((cat) =>
        fetch(`${BASE_URL}/api/admin/getcampaigns?campaignCategory=${cat}`)
          .then((r) => r.json())
          .then((d) => (d.success ? d.data || [] : []))
          .catch(() => [])
      )
    )
      .then((results) => {
        const completed = results.flat().filter(
          (c) =>
            c.status === "completed" ||
            c.status === "ended" ||
            new Date(c.endDate) < new Date()
        );
        setCampaigns(completed);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return { campaigns, loading, error };
};

/* ═══════════════════════════════════════════════════
   WINNERS MODAL
═══════════════════════════════════════════════════ */
const WinnersModal = ({ campaign, onClose }) => {
  /* lock body scroll while open */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  /* close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const hasWinners = campaign.winners?.length > 0;

  return (
    <AnimatePresence>
      {/* BACKDROP */}
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ background: "rgba(60,40,5,0.55)", backdropFilter: "blur(6px)" }}
      >
        {/* PANEL — stop propagation so clicking inside doesn't close */}
        <motion.div
          key="panel"
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.97 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full flex flex-col overflow-hidden"
          style={{
            maxWidth: 560,
            maxHeight: "92dvh",
            background: `linear-gradient(165deg,${G.cream} 0%,${G.creamDeep} 60%,${G.creamMid} 100%)`,
            border: `1.5px solid ${G.creamBorder}`,
            borderRadius: "24px 24px 24px 24px",
            boxShadow: `0 24px 80px rgba(80,50,0,0.28), 0 0 0 1px ${G.creamBorder}`,
          }}
        >
          {/* ── MODAL HEADER ── */}
          <div
            className="flex-shrink-0 px-5 pt-5 pb-4"
            style={{ borderBottom: `1.5px solid ${G.creamBorder}` }}
          >
            {/* drag handle (mobile) */}
            <div className="flex justify-center mb-3 sm:hidden">
              <div className="w-10 h-1 rounded-full" style={{ background: G.creamBorder }} />
            </div>

            <div className="flex items-start gap-3">
              {/* campaign thumb */}
              <div
                className="flex-shrink-0 rounded-2xl overflow-hidden"
                style={{ width: 56, height: 56 }}
              >
                {campaign.images?.[0] ? (
                  <img
                    src={`${BASE_URL}${campaign.images[0]}`}
                    alt={campaign.title}
                    className="w-full h-full object-cover"
                    style={{ filter: "grayscale(20%)" }}
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: G.creamMid }}
                  >
                    <FaTrophy style={{ color: G.goldLight, fontSize: 20 }} />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: G.textLight }}>
                  Campaign Results
                </p>
                <h2
                  className="font-extrabold leading-tight truncate"
                  style={{ color: G.text, fontSize: "clamp(15px,3vw,19px)" }}
                >
                  {campaign.title}
                </h2>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: G.textLight }}>
                    <FaCalendarAlt style={{ fontSize: 9 }} />
                    Ended {new Date(campaign.endDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: G.textLight }}>
                    <FaUsers style={{ fontSize: 9 }} />
                    {campaign.noOfWinners} winner{campaign.noOfWinners !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {/* close btn */}
              <button
                onClick={onClose}
                className="flex-shrink-0 flex items-center justify-center rounded-full transition-all hover:scale-105"
                style={{
                  width: 32, height: 32,
                  background: G.creamMid,
                  border: `1px solid ${G.creamBorder}`,
                }}
              >
                <FaTimes style={{ color: G.goldDeep, fontSize: 11 }} />
              </button>
            </div>
          </div>

          {/* ── MODAL BODY (scrollable) ── */}
          <div
            className="flex-1 overflow-y-auto px-5 py-4"
            style={{ scrollbarWidth: "thin", scrollbarColor: `${G.creamBorder} transparent` }}
          >
            {/* prizes summary strip */}
            {campaign.prizes?.length > 0 && (
              <div className="mb-4">
                <p className="text-[9px] font-black uppercase tracking-widest mb-2" style={{ color: G.textLight }}>
                  Prize Pool
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {campaign.prizes.map((p) => (
                    <div
                      key={p._id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                      style={{ background: G.creamMid, color: G.goldDeep, border: `1px solid ${G.creamBorder}` }}
                    >
                      <FaGift style={{ color: G.gold, fontSize: 8 }} />
                      {p.prizeName}
                      {p.prizeType === "cash" && p.prizeAmount ? ` — ₹${p.prizeAmount.toLocaleString()}` : p.description ? ` — ${p.description}` : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* winners list */}
            {hasWinners ? (
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest mb-3" style={{ color: G.textLight }}>
                  🏆 Winners
                </p>
                <div className="flex flex-col gap-3">
                  {campaign.winners.map((winner, idx) => {
                    const pos   = winner.prizePosition || idx + 1;
                    const medal = MEDAL[pos] || MEDAL[3];

                    return (
                      <motion.div
                        key={winner._id}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.07 }}
                        className="flex items-center gap-3 rounded-2xl p-3"
                        style={{
                          background: pos === 1
                            ? "linear-gradient(135deg,#FFFBE8,#FFF3CC)"
                            : G.creamDeep,
                          border: `1.5px solid ${pos === 1 ? G.creamBorder : G.creamBorder + "88"}`,
                          boxShadow: pos === 1 ? `0 4px 16px ${G.goldGlow}` : "none",
                        }}
                      >
                        {/* position medal */}
                        <div
                          className="flex-shrink-0 flex items-center justify-center rounded-xl font-black text-xs"
                          style={{
                            width: 36, height: 36,
                            background: medal.bg,
                            color: medal.color,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          }}
                        >
                          {pos <= 3 ? <FaMedal style={{ fontSize: 14 }} /> : pos}
                        </div>

                        {/* winner info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-extrabold text-sm leading-none truncate" style={{ color: G.text }}>
                            {winner.userName}
                          </p>
                          <p className="text-[11px] mt-0.5 truncate" style={{ color: G.textMid }}>
                            {winner.winningGift || winner.prizeName}
                          </p>
                          {winner.ticketIds?.length > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <FaTicketAlt style={{ color: G.textLight, fontSize: 8 }} />
                              <span className="text-[10px]" style={{ color: G.textLight }}>
                                {winner.ticketIds.join(", ")}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* right side — position label + status */}
                        <div className="flex-shrink-0 flex flex-col items-end gap-1">
                          <span
                            className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider"
                            style={{ background: medal.bg, color: medal.color }}
                          >
                            {medal.label} Prize
                          </span>
                          {winner.status === "claimed" ? (
                            <span
                              className="px-2 py-0.5 rounded-full text-[9px] font-bold"
                              style={{ background: "#DCFCE7", color: "#166534" }}
                            >
                              ✓ Claimed
                            </span>
                          ) : (
                            <span
                              className="px-2 py-0.5 rounded-full text-[9px] font-bold"
                              style={{ background: "#FEF9C3", color: "#854D0E" }}
                            >
                              Pending
                            </span>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* no winners yet */
              <div
                className="flex flex-col items-center justify-center py-10 rounded-2xl"
                style={{ background: G.creamMid, border: `1px solid ${G.creamBorder}` }}
              >
                <FaTrophy style={{ color: G.goldLight, fontSize: 32, opacity: 0.5, marginBottom: 10 }} />
                <p className="font-bold text-sm" style={{ color: G.textMid }}>No winners announced yet</p>
                <p className="text-xs mt-1" style={{ color: G.textLight }}>Check back soon!</p>
              </div>
            )}
          </div>

          {/* ── MODAL FOOTER ── */}
          <div
            className="flex-shrink-0 px-5 py-4"
            style={{ borderTop: `1.5px solid ${G.creamBorder}` }}
          >
            <button
              onClick={onClose}
              className="w-full rounded-2xl font-extrabold text-sm py-3 transition-all hover:scale-[1.01]"
              style={{
                background: `linear-gradient(135deg,${G.goldLight},${G.gold},${G.goldDeep})`,
                color: "#3B2A0A",
                boxShadow: `0 4px 16px ${G.goldGlow}`,
              }}
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ═══════════════════════════════════════════════════
   SKELETON CARD
═══════════════════════════════════════════════════ */
const SkeletonCard = () => (
  <div
    className="flex-shrink-0 rounded-3xl overflow-hidden animate-pulse border"
    style={{ width: "clamp(260px,30vw,320px)", background: G.creamDeep, borderColor: G.creamBorder }}
  >
    <div style={{ height: 180, background: G.creamMid }} />
    <div className="p-5 space-y-3">
      <div className="h-4 rounded-lg w-3/4" style={{ background: G.creamMid }} />
      <div className="h-3 rounded-lg w-1/2" style={{ background: G.creamBorder }} />
      <div className="h-10 rounded-2xl w-full mt-2" style={{ background: G.creamMid }} />
      <div className="h-10 rounded-2xl w-full" style={{ background: G.creamMid }} />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════
   PRIZE PILL (card use)
═══════════════════════════════════════════════════ */
const PrizePill = ({ prize }) => (
  <div
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
    style={{ background: G.creamMid, color: G.goldDeep, border: `1px solid ${G.creamBorder}` }}
  >
    <FaTrophy style={{ color: G.gold, fontSize: 8, flexShrink: 0 }} />
    <span className="truncate" style={{ maxWidth: 120 }}>
      {prize.prizeName}
      {prize.prizeType === "cash" && prize.prizeAmount
        ? ` — ₹${prize.prizeAmount.toLocaleString()}`
        : prize.description ? ` — ${prize.description}` : ""}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════
   CAMPAIGN CARD
═══════════════════════════════════════════════════ */
const CampaignCard = ({ campaign, index }) => {
  const navigate = useNavigate();
  const [imgError, setImgError]       = useState(false);
  const [showModal, setShowModal]     = useState(false);
  const hasImage  = campaign.images?.[0] && !imgError;
  const hasWinners = campaign.winners?.length > 0;

  const categoryLabel = {
    daily:   "🔥 DAILY",
    weekly:  "⚡ WEEKLY",
    monthly: "👑 MONTHLY",
  }[campaign.campaignCategory] || "ENDED";

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
        className="flex-shrink-0 rounded-3xl overflow-hidden flex flex-col"
        style={{
          width: "clamp(260px,30vw,320px)",
          background: `linear-gradient(165deg,${G.cream} 0%,${G.creamDeep} 55%,${G.creamMid} 100%)`,
          border: `1.5px solid ${G.creamBorder}`,
          boxShadow: `0 4px 24px rgba(201,168,76,0.13),0 1px 6px rgba(0,0,0,0.04)`,
        }}
      >
        {/* IMAGE */}
        <div className="relative flex-shrink-0 overflow-hidden" style={{ height: 180, background: G.creamMid }}>
          {hasImage ? (
            <img
              src={`${BASE_URL}${campaign.images[0]}`}
              alt={campaign.title}
              onError={() => setImgError(true)}
              className="w-full h-full object-cover"
              style={{ filter: "grayscale(25%) brightness(0.92)" }}
            />
          ) : (
            <div
              className="w-full h-full flex flex-col items-center justify-center"
              style={{ background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})` }}
            >
              <FaTrophy style={{ color: G.goldLight, fontSize: 40, opacity: 0.4, marginBottom: 6 }} />
              <span className="text-xs font-medium" style={{ color: G.textLight }}>No Image</span>
            </div>
          )}
          {/* overlay */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top,rgba(72,48,4,0.72) 0%,transparent 52%)" }} />
          {/* category badge */}
          <div
            className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-black tracking-widest"
            style={{ background: "rgba(255,252,235,0.93)", color: G.goldDeep, border: `1px solid ${G.creamBorder}`, backdropFilter: "blur(10px)" }}
          >
            {categoryLabel}
          </div>
          {/* ended badge */}
          <div
            className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: "rgba(255,252,235,0.93)", border: `1px solid ${G.creamBorder}`, backdropFilter: "blur(10px)" }}
          >
            <FaLock style={{ color: "#9B7A3A", fontSize: 8 }} />
            <span className="text-[10px] font-bold" style={{ color: G.textMid }}>ENDED</span>
          </div>
          {/* title */}
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
            <h3
              className="font-extrabold leading-snug"
              style={{ color: "#FFF8E7", textShadow: "0 1px 8px rgba(0,0,0,0.5)", fontSize: "clamp(13px,2vw,16px)" }}
            >
              {campaign.title}
            </h3>
          </div>
        </div>

        {/* BODY */}
        <div className="flex flex-col flex-1 p-4 gap-3">

          {/* end date */}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-2xl"
            style={{ background: G.creamMid, border: `1px solid ${G.creamBorder}` }}
          >
            <FaCalendarAlt style={{ color: G.goldDeep, fontSize: 10, flexShrink: 0 }} />
            <span className="text-xs font-semibold" style={{ color: G.textMid }}>
              Ended on{" "}
              <span style={{ color: G.goldDeep }}>
                {new Date(campaign.endDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </span>
            </span>
          </div>

          {/* winners preview OR prizes */}
          {hasWinners ? (
            <div>
              <p className="font-black uppercase tracking-widest mb-1.5" style={{ color: G.textLight, fontSize: 9 }}>
                🏆 Winners
              </p>
              <div className="flex flex-col gap-1.5">
                {campaign.winners.slice(0, 2).map((w, i) => {
                  const pos   = w.prizePosition || i + 1;
                  const medal = MEDAL[pos] || MEDAL[3];
                  return (
                    <div
                      key={w._id}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl"
                      style={{ background: G.creamMid, border: `1px solid ${G.creamBorder}` }}
                    >
                      <div
                        className="flex-shrink-0 flex items-center justify-center rounded-lg"
                        style={{ width: 24, height: 24, background: medal.bg }}
                      >
                        <FaMedal style={{ fontSize: 10, color: medal.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-xs truncate" style={{ color: G.text }}>{w.userName}</p>
                        <p className="text-[10px] truncate" style={{ color: G.textLight }}>{w.winningGift}</p>
                      </div>
                      {w.status === "claimed" && (
                        <span
                          className="flex-shrink-0 px-1.5 py-0.5 rounded-full font-bold"
                          style={{ background: "#DCFCE7", color: "#166534", fontSize: 8 }}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                  );
                })}
                {campaign.winners.length > 2 && (
                  <p className="text-[10px] font-semibold px-1" style={{ color: G.textLight }}>
                    +{campaign.winners.length - 2} more winner{campaign.winners.length - 2 !== 1 ? "s" : ""}
                  </p>
                )}
              </div>
            </div>
          ) : campaign.prizes?.length > 0 ? (
            <div>
              <p className="font-black uppercase tracking-widest mb-1.5" style={{ color: G.textLight, fontSize: 9 }}>
                Prizes
              </p>
              <div className="flex flex-wrap gap-1.5">
                {campaign.prizes.slice(0, 2).map((p) => <PrizePill key={p._id} prize={p} />)}
              </div>
            </div>
          ) : null}

          {/* price + tickets */}
          <div
            className="flex items-end justify-between mt-auto pt-3"
            style={{ borderTop: `1px solid ${G.creamBorder}` }}
          >
            <div>
              <p className="font-semibold uppercase tracking-wider mb-0.5" style={{ color: G.textLight, fontSize: 9 }}>Ticket Price</p>
              <p className="text-xl font-black leading-none" style={{ color: G.goldDeep }}>₹{campaign.price}</p>
            </div>
            <div className="text-right">
              <p className="font-semibold uppercase tracking-wider mb-0.5" style={{ color: G.textLight, fontSize: 9 }}>Total Tickets</p>
              <p className="text-sm font-bold" style={{ color: G.text }}>{campaign.noOfTickets.toLocaleString()}</p>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex gap-2">
            {/* View Winners */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowModal(true)}
              className="flex-1 rounded-2xl font-extrabold text-xs flex items-center justify-center gap-1.5"
              style={{
                background: `linear-gradient(135deg,${G.goldLight},${G.gold},${G.goldDeep})`,
                color: "#3B2A0A",
                boxShadow: `0 4px 14px ${G.goldGlow}`,
                paddingTop: 11,
                paddingBottom: 11,
              }}
            >
              <FaTrophy style={{ fontSize: 10 }} />
              {hasWinners ? "View Winners" : "View Results"}
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* MODAL */}
      {showModal && (
        <WinnersModal campaign={campaign} onClose={() => setShowModal(false)} />
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════
   EMPTY STATE
═══════════════════════════════════════════════════ */
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <div
      className="rounded-full flex items-center justify-center mb-5 shadow-md"
      style={{ width: 72, height: 72, background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})`, border: `1.5px solid ${G.creamBorder}` }}
    >
      <FaTrophy style={{ color: G.goldLight, fontSize: 28 }} />
    </div>
    <p className="font-bold text-base mb-1" style={{ color: G.text }}>No Completed Campaigns</p>
    <p className="text-sm" style={{ color: G.textLight }}>Past campaigns will appear here once they end.</p>
  </div>
);

/* ═══════════════════════════════════════════════════
   ARROW BUTTON
═══════════════════════════════════════════════════ */
const ArrowBtn = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="hidden md:flex items-center justify-center rounded-full transition-all hover:scale-105"
    style={{
      width: 40, height: 40,
      background: "rgba(255,252,235,0.95)",
      border: `1.5px solid ${G.creamBorder}`,
      boxShadow: `0 2px 12px ${G.goldGlow}`,
      backdropFilter: "blur(10px)",
    }}
  >
    {children}
  </button>
);

/* ═══════════════════════════════════════════════════
   MAIN EXPORT
═══════════════════════════════════════════════════ */
const CompletedCampaigns = () => {
  const scrollRef                     = useRef();
  const [showLeft, setShowLeft]       = useState(false);
  const [showRight, setShowRight]     = useState(true);
  const { campaigns, loading, error } = useCompletedCampaigns();

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 10);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [campaigns, checkScroll]);

  const scroll = (dir) =>
    scrollRef.current?.scrollBy({ left: dir === "left" ? -360 : 360, behavior: "smooth" });

  if (!loading && !error && campaigns.length === 0) return null;

  return (
    <section
      className="relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg,${G.cream} 0%,${G.creamDeep} 50%,${G.cream} 100%)`,
        paddingTop: "clamp(48px,8vw,96px)",
        paddingBottom: "clamp(48px,8vw,96px)",
      }}
    >
      {/* bg orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute rounded-full" style={{ top: -140, right: -140, width: 420, height: 420, background: "radial-gradient(circle,rgba(240,217,140,0.25),transparent 70%)" }} />
        <div className="absolute rounded-full" style={{ bottom: -140, left: -140, width: 420, height: 420, background: "radial-gradient(circle,rgba(240,217,140,0.25),transparent 70%)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* HEADER */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full mb-5"
            style={{
              background: G.creamMid, color: G.goldDeep,
              border: `1.5px solid ${G.creamBorder}`,
              boxShadow: `0 2px 14px ${G.goldGlow}`,
              padding: "7px 18px", fontSize: 10, fontWeight: 900,
              letterSpacing: "0.12em", textTransform: "uppercase",
            }}
          >
            <FaTrophy style={{ color: G.gold, fontSize: 9 }} />
            Completed Campaigns
            <FaTrophy style={{ color: G.gold, fontSize: 9 }} />
          </motion.div>

          <div className="flex items-center justify-center gap-2.5 mt-7">
            <div style={{ height: 1, width: "clamp(32px,6vw,96px)", background: `linear-gradient(to right,transparent,${G.creamBorder})` }} />
            <FaStar style={{ color: G.goldLight, fontSize: 9 }} />
            <div style={{ height: 1, width: 20, background: G.creamBorder }} />
            <FaTrophy style={{ color: G.gold, fontSize: 12 }} />
            <div style={{ height: 1, width: 20, background: G.creamBorder }} />
            <FaStar style={{ color: G.goldLight, fontSize: 9 }} />
            <div style={{ height: 1, width: "clamp(32px,6vw,96px)", background: `linear-gradient(to left,transparent,${G.creamBorder})` }} />
          </div>
        </div>

        {/* SCROLL AREA */}
        <div className="relative">
          <AnimatePresence>
            {showLeft && !loading && campaigns.length > 0 && (
              <motion.div key="left" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10">
                <ArrowBtn onClick={() => scroll("left")}>
                  <FaChevronLeft style={{ color: G.goldDeep, fontSize: 13 }} />
                </ArrowBtn>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showRight && !loading && campaigns.length > 0 && (
              <motion.div key="right" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10">
                <ArrowBtn onClick={() => scroll("right")}>
                  <FaChevronRight style={{ color: G.goldDeep, fontSize: 13 }} />
                </ArrowBtn>
              </motion.div>
            )}
          </AnimatePresence>

          {!loading && error && (
            <div className="rounded-2xl p-4 text-sm border text-center"
              style={{ background: "#FFF5F5", borderColor: "#FFCDD2", color: "#C62828" }}>
              ⚠️ {error}
            </div>
          )}

          {loading && (
            <div className="flex gap-5 overflow-hidden pb-2">
              {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {!loading && !error && campaigns.length === 0 && <EmptyState />}

          {!loading && !error && campaigns.length > 0 && (
            <div
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto pb-3"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <style>{`div::-webkit-scrollbar{display:none}`}</style>
              {campaigns.map((c, i) => (
                <CampaignCard key={c._id} campaign={c} index={i} />
              ))}
              <div className="flex-shrink-0 w-2" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CompletedCampaigns;